import { Navigate, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useAuthState } from '@/lib/auth-state'
import { readLastProjectId, resolveLandingProjectId } from '@/lib/last-project'
import { legacySettingsSection, type SettingsSection } from '@/lib/settings-dialog'
import { SettingsDialogView, SettingsPaneContent } from './settings-dialog'

/**
 * Settings are a dialog now (board h39z); the old settings URLs keep working by
 * opening the project's chat with the dialog on the matching section. All
 * replace, so Back leaves the app the way the link came in.
 */

/** `beforeLoad` of `/$projectId/settings/$` (`/settings`, `/settings/project|members|organization`). */
export function redirectLegacyProjectSettings({
  params,
}: {
  params: { projectId: string; _splat?: string }
}): never {
  throw redirect({
    to: '/$projectId/chat',
    params: { projectId: params.projectId },
    search: { settings: legacySettingsSection(params._splat) },
    replace: true,
  })
}

/** `beforeLoad` of the old `/$projectId/members` page. */
export function redirectLegacyMembers({ params }: { params: { projectId: string } }): never {
  throw redirect({
    to: '/$projectId/chat',
    params: { projectId: params.projectId },
    search: { settings: 'members' },
    replace: true,
  })
}

/**
 * The old `/settings` profile page: the landing project (same pick as the app
 * root) with the dialog on Profil. A user without any project gets the dialog
 * on its own, with the account sections only; closing goes to `/`.
 */
export function SettingsRedirect() {
  const { me, status } = useAuthState()

  if (status === 'loading' || !me) return null

  const projectId = resolveLandingProjectId({
    serverProjectId: me.project?.id,
    memberProjectIds: me.memberships.projects.map((p) => p.projectId),
    storedProjectId: readLastProjectId(me.user.id),
  })

  if (projectId) {
    return (
      <Navigate
        to="/$projectId/chat"
        params={{ projectId }}
        search={{ settings: 'profile' }}
        replace
      />
    )
  }

  return <StandaloneSettings />
}

function StandaloneSettings() {
  const navigate = useNavigate()
  const [section, setSection] = useState<SettingsSection>('profile')

  return (
    <div className="min-h-screen bg-background">
      <SettingsDialogView
        section={section}
        project={null}
        onSwitch={setSection}
        onClose={() => void navigate({ to: '/' })}
        renderPane={(pane) => <SettingsPaneContent pane={pane} projectId={null} />}
      />
    </div>
  )
}
